// ResistorCalculator.js
import React, { useState, useEffect } from "react";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import band_3 from "../../../images/3-band.webp";
import band_4 from "../../../images/4-band.webp";
import band_5 from "../../../images/5-band.webp";
import band_6 from "../../../images/6-band.webp";
import SharedHelmet from "../Helmet";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import ParallelResistorCalculator from "./ParallelResistorCalculator";
import SeriesResistorCalculator from "./ResistorsInSeriesCalculator";
import ResistanceCalculator from "./CundoctorResistanceCalculator";
import { isMobile } from "react-device-detect";

const TITLE = "Kalkulator Resistansi - Hitung Nilai Resistansi dengan Mudah";
const DESCRIPTION =
  "Kalkulator ini membantu Anda mengetahui nilai resistansi berdasarkan warnanya, dan menghitung resistansi sekelompok resistor yang terhubung secara seri atau paralel, serta resistansi konduktor berdasarkan ukuran dan konduktivitasnya.";
const KEYWORDS =
  "kalkulator resistansi, kalkulator warna, resistor listrik, menghitung nilai resistansi, resistansi resistor paralel, resistansi resistor seri, menghitung resistansi konduktor";
const OG_URL = "https://do-calculate.com/calculator/id/resistor-calculator";

const colorCodes = {
  black:  { value: 0, multiplier: 1,          tolerance: null,     tempCoeff: "250ppm/K", colorAr: "Hitam",    hex: "#1a1a1a", textColor: "#fff" },
  brown:  { value: 1, multiplier: 10,         tolerance: "±1%",    tempCoeff: "100ppm/K", colorAr: "Coklat",    hex: "#795548", textColor: "#fff" },
  red:    { value: 2, multiplier: 100,        tolerance: "±2%",    tempCoeff: "50ppm/K",  colorAr: "Merah",    hex: "#e53935", textColor: "#fff" },
  orange: { value: 3, multiplier: 1000,       tolerance: "±0.05%", tempCoeff: "15ppm/K",  colorAr: "Oranye", hex: "#fb8c00", textColor: "#fff" },
  yellow: { value: 4, multiplier: 10000,      tolerance: "±0.02%", tempCoeff: "25ppm/K",  colorAr: "Kuning",    hex: "#fdd835", textColor: "#333" },
  green:  { value: 5, multiplier: 100000,     tolerance: "±0.5%",  tempCoeff: "20ppm/K",  colorAr: "Hijau",    hex: "#43a047", textColor: "#fff" },
  blue:   { value: 6, multiplier: 1000000,    tolerance: "±0.25%", tempCoeff: "10ppm/K",  colorAr: "Biru",    hex: "#1e88e5", textColor: "#fff" },
  violet: { value: 7, multiplier: 10000000,   tolerance: "±0.1%",  tempCoeff: "5ppm/K",   colorAr: "Ungu",   hex: "#8e24aa", textColor: "#fff" },
  grey:   { value: 8, multiplier: 100000000,  tolerance: "±0.01%", tempCoeff: "1ppm/K",   colorAr: "Abu-abu",   hex: "#757575", textColor: "#fff" },
  white:  { value: 9, multiplier: 1000000000, tolerance: null,     tempCoeff: null,        colorAr: "Putih",    hex: "#f5f5f5", textColor: "#333" },
  gold:   { value: null, multiplier: 0.1,    tolerance: "±5%",    tempCoeff: null,        colorAr: "Emas",    hex: "#d4a017", textColor: "#333" },
  silver: { value: null, multiplier: 0.01,   tolerance: "±10%",   tempCoeff: null,        colorAr: "Perak",     hex: "#aaaaaa", textColor: "#333" },
};

// Format resistance with proper units
function formatResistance(ohms) {
  if (ohms === null || ohms === undefined || isNaN(ohms)) return { value: "Error", unit: "" };
  if (ohms >= 1_000_000) return { value: (ohms / 1_000_000).toLocaleString("en-US", { maximumFractionDigits: 2 }), unit: "MΩ" };
  if (ohms >= 1_000)     return { value: (ohms / 1_000).toLocaleString("en-US",     { maximumFractionDigits: 2 }), unit: "kΩ" };
  return { value: ohms.toLocaleString("en-US", { maximumFractionDigits: 2 }), unit: "Ω" };
}

// Live SVG resistor — bands update as colors are picked
function ResistorSVG({ numBands, bandColors, bands }) {
  const visibleBands = bands.filter((b) => b.show);

  const bodyWidth  = 160;
  const bodyHeight = 40;
  const bodyX      = 65;
  const bodyY      = 30;
  const svgWidth   = 290;
  const svgHeight  = 100;
  const wireY      = bodyY + bodyHeight / 2;

  const bandWidth  = 12;
  const padding    = 16;
  const usableWidth = bodyWidth - padding * 2;
  const count = visibleBands.length;
  const bandSpacing = count > 1 ? usableWidth / (count - 1) : 0;

  return (
    <svg
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      style={{ width: "100%", maxWidth: "340px", margin: "0 auto", display: "block" }}
      aria-label="resistor preview"
    >
      {/* Wires */}
      <line x1="0"                  y1={wireY} x2={bodyX}              y2={wireY} stroke="#999" strokeWidth="3" strokeLinecap="round" />
      <line x1={bodyX + bodyWidth}  y1={wireY} x2={svgWidth}           y2={wireY} stroke="#999" strokeWidth="3" strokeLinecap="round" />

      {/* Body shadow */}
      <rect x={bodyX + 3} y={bodyY + 3} width={bodyWidth} height={bodyHeight} rx="9" fill="rgba(0,0,0,0.12)" />
      {/* Body fill */}
      <rect x={bodyX} y={bodyY} width={bodyWidth} height={bodyHeight} rx="9" fill="#d4c89a" />

      {/* Color bands */}
      {visibleBands.map((band, i) => {
        const hex = colorCodes[bandColors[band.key]]?.hex || "#ccc";
        const x   = bodyX + padding + (count === 1 ? 0 : i * bandSpacing) - bandWidth / 2;
        // clip band to body bounds
        const clippedX = Math.max(bodyX, Math.min(x, bodyX + bodyWidth - bandWidth));
        return (
          <rect
            key={band.key}
            x={clippedX}
            y={bodyY}
            width={bandWidth}
            height={bodyHeight}
            fill={hex}
            style={{ transition: "fill 0.2s ease" }}
          />
        );
      })}

      {/* Body border on top */}
      <rect x={bodyX} y={bodyY} width={bodyWidth} height={bodyHeight} rx="9" fill="none" stroke="#b5a87a" strokeWidth="1.5" />

      {/* Terminal dots */}
      <circle cx={bodyX}             cy={wireY} r="4" fill="#888" />
      <circle cx={bodyX + bodyWidth} cy={wireY} r="4" fill="#888" />
    </svg>
  );
}

// Swatch color picker for one band
function SwatchPicker({ label, bandKey, selected, onChange, allowedColors }) {
  const colors = allowedColors
    ? Object.keys(colorCodes).filter((c) => allowedColors.includes(c))
    : Object.keys(colorCodes);

  return (
    <div style={{ marginBottom: "18px", paddingBottom: "18px", borderBottom: "1px solid #f0f0f0" }}>
      <div style={{ fontWeight: 700, fontSize: "13px", color: "#333", marginBottom: "10px" }}>
        {label}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
        {colors.map((color) => {
          const isSelected = selected === color;
          return (
            <button
              key={color}
              title={colorCodes[color].colorAr}
              onClick={() => onChange(bandKey, color)}
              aria-label={colorCodes[color].colorAr}
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                border: "none",
                cursor: "pointer",
                backgroundColor: colorCodes[color].hex,
                boxShadow: isSelected
                  ? `0 0 0 3px #fff, 0 0 0 5px ${colorCodes[color].hex}`
                  : "0 1px 3px rgba(0,0,0,0.3)",
                transform: isSelected ? "scale(1.15)" : "scale(1)",
                transition: "transform 0.12s, box-shadow 0.12s",
                position: "relative",
                flexShrink: 0,
              }}
            >
              {isSelected && (
                <span style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                  fontWeight: 900,
                }}>
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Selected color label pill */}
      <div style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 600,
        backgroundColor: colorCodes[selected]?.hex || "#ccc",
        color: colorCodes[selected]?.textColor || "#333",
        transition: "background-color 0.2s",
      }}>
        {colorCodes[selected]?.colorAr || "—"}
        {colorCodes[selected]?.value !== null && colorCodes[selected]?.value !== undefined
          ? ` (${colorCodes[selected].value})`
          : ""}
      </div>
    </div>
  );
}

function ResistorCalculator() {
  const [numBands, setNumBands] = useState(4);
  const [bandColors, setBandColors] = useState({
    first:      "brown",
    second:     "black",
    third:      "black",
    multiplier: "red",
    tolerance:  "gold",
    tempCoeff:  "brown",
  });
  const [result, setResult] = useState(null);

  const handleColorChange = (band, color) => {
    setBandColors((prev) => ({ ...prev, [band]: color }));
  };

  // Band definitions
  const bands = [
    { key: "first",      label: "Warna Pita Pertama",   show: true },
    { key: "second",     label: "Warna Pita Kedua",  show: true },
    { key: "third",      label: "Warna Pita Ketiga",  show: numBands >= 5 },
    { key: "multiplier", label: "Warna Pengali",        show: true },
    { key: "tolerance",  label: "Warna Toleransi",        show: numBands >= 4 },
    { key: "tempCoeff",  label: "Warna Koefisien Suhu",  show: numBands === 6 },
  ];

  const digitColors     = Object.keys(colorCodes).filter((c) => colorCodes[c].value !== null);
  const toleranceColors = Object.keys(colorCodes).filter((c) => colorCodes[c].tolerance !== null);
  const tempCoeffColors = Object.keys(colorCodes).filter((c) => colorCodes[c].tempCoeff !== null);

  const getAllowedColors = (key) => {
    if (["first", "second", "third"].includes(key)) return digitColors;
    if (key === "tolerance") return toleranceColors;
    if (key === "tempCoeff") return tempCoeffColors;
    return null;
  };

  // Auto-calculate on every change
  useEffect(() => {
    const firstDigit  = colorCodes[bandColors.first]?.value;
    const secondDigit = colorCodes[bandColors.second]?.value;
    const thirdDigit  = numBands >= 5 ? colorCodes[bandColors.third]?.value : 0;
    const multiplier  = colorCodes[bandColors.multiplier]?.multiplier;
    const tolerance   = colorCodes[bandColors.tolerance]?.tolerance;
    const tempCoeff   = numBands === 6 ? colorCodes[bandColors.tempCoeff]?.tempCoeff : null;

    let resistance = null;
    if (
      firstDigit  !== null && firstDigit  !== undefined &&
      secondDigit !== null && secondDigit !== undefined &&
      multiplier  !== null && multiplier  !== undefined
    ) {
      if (numBands === 3 || numBands === 4) {
        resistance = (firstDigit * 10 + secondDigit) * multiplier;
      } else {
        resistance = (firstDigit * 100 + secondDigit * 10 + thirdDigit) * multiplier;
      }
    }

    const formatted = formatResistance(resistance);
    const tolerancePct = parseFloat(tolerance);
    let minR = null, maxR = null;
    if (resistance !== null && !isNaN(tolerancePct)) {
      minR = formatResistance(resistance * (1 - tolerancePct / 100));
      maxR = formatResistance(resistance * (1 + tolerancePct / 100));
    }

    setResult({ formatted, tolerance, tempCoeff, minR, maxR });
  }, [bandColors, numBands]);

  return (
    <div>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />

      <style>{`
        .rc-result-card {
          background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%);
          border-radius: 12px;
          padding: 24px;
          color: #fff;
          margin-top: 16px;
        }
        .rc-result-value {
          font-size: 42px;
          font-weight: 700;
          letter-spacing: -1px;
          line-height: 1;
        }
        .rc-result-unit {
          font-size: 22px;
          font-weight: 400;
          opacity: 0.85;
          margin-right: 4px;
        }
        .rc-result-row {
          display: flex;
          gap: 12px;
          margin-top: 14px;
          flex-wrap: wrap;
        }
        .rc-result-badge {
          background: rgba(255,255,255,0.15);
          border-radius: 8px;
          padding: 8px 14px;
          font-size: 13px;
          line-height: 1.4;
        }
        .rc-result-badge b {
          display: block;
          opacity: 0.75;
          font-size: 11px;
          margin-bottom: 2px;
        }
        .rc-band-chip {
          width: 26px;
          height: 26px;
          border-radius: 6px;
          border: 2px solid rgba(255,255,255,0.4);
          display: inline-block;
          transition: background-color 0.2s;
        }
        .rc-bands-btn {
          padding: 8px 18px;
          border-radius: 8px;
          border: 2px solid #e0e0e0;
          background: #fff;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          color: #555;
          transition: all 0.15s;
        }
        .rc-bands-btn.active {
          border-color: #2e7d32;
          background: #2e7d32;
          color: #fff;
        }
        .rc-bands-btn:hover:not(.active) {
          border-color: #2e7d32;
          color: #2e7d32;
        }
        .rc-svg-wrapper {
          background: #f5f5f0;
          border-radius: 12px;
          padding: 20px 16px 12px;
          margin-bottom: 8px;
          text-align: center;
        }
        .rc-svg-label {
          font-size: 11px;
          color: #999;
          margin-top: 6px;
          text-align: center;
        }
      `}</style>

      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ height: "50px" }}>
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>

          <div className="border border-light">
            {/* ===== ORIGINAL INTRO TEXT ===== */}
            <div className="row">
              <div>
                <p>
                  Kalkulator resistansi canggih ini membantu Anda menghitung nilai resistansi dengan akurat
                  berdasarkan warna yang ditentukan pada resistor. Gunakan
                  alat komprehensif ini untuk menentukan nilai resistansi dengan akurat, dengan kemampuan menghitung
                  resistansi sekelompok resistor yang terhubung secara seri atau paralel,
                  serta menghitung resistansi konduktor berdasarkan ukuran dan konduktivitasnya.
                </p>
                <ul>
                  <li>Pilih jumlah pita pada resistor (misalnya 4 pita, 5 pita, dll).</li>
                  <li>Tentukan warna untuk setiap pita sesuai dengan kode warna standar resistor.</li>
                  <li>Hasil akan muncul secara otomatis setelah Anda memilih warna.</li>
                  <li>
                    Hasil akan ditampilkan di bawah, termasuk detail tambahan seperti resistansi
                    total sekelompok resistor jika Anda melakukan perhitungan untuk pengaturan
                    yang berbeda.
                  </li>
                </ul>
                <p className="alert alert-info">
                  <b>Cara Membaca Pita Resistor:</b>
                  <br />
                  Untuk menentukan nilai resistansi dengan benar, Anda harus selalu mulai membaca dari ujung yang memiliki pita yang berdekatan (yang lebih dekat satu sama lain). Pita pertama biasanya lebih dekat ke salah satu ujung resistor, sedangkan pita toleransi (biasanya emas atau perak) dipisahkan dengan jarak yang lebih besar dari pita lainnya.
                  <br />
                  <b>Pita Pertama:</b> Yang paling dekat dengan salah satu ujung resistor.
                  <br />
                  <b>Pita Toleransi:</b> Biasanya berwarna emas atau perak dan dipisahkan dengan jarak yang lebih besar dari pita lainnya. Pita ini menunjukkan akhir pembacaan.
                </p>
                Manfaatkan alat ini untuk mendapatkan perhitungan akurat untuk proyek elektronik Anda
                dan menentukan nilai yang sesuai untuk komponen sirkuit listrik Anda.
                <hr />
              </div>
            </div>

            <MobileUnderCalAds />

            {/* ===== CALCULATOR ROW ===== */}
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />

                <div >
                  {/* Band count — pill buttons */}
                  <div className="form-group">
                    <b><label >Jumlah Pita</label></b>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px", marginBottom: "20px" }}>
                      {[3, 4, 5, 6].map((n) => (
                        <button
                          key={n}
                          className={`rc-bands-btn${numBands === n ? " active" : ""}`}
                          onClick={() => setNumBands(n)}
                        >
                          {n} Pita
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ── LIVE SVG RESISTOR ── updates as you pick colors */}
                  <div className="rc-svg-wrapper">
                    <ResistorSVG numBands={numBands} bandColors={bandColors} bands={bands} />
                    <div className="rc-svg-label">Pratinjau Langsung Resistor</div>
                  </div>

                  <br />

                  {/* Color swatch pickers */}
                  {bands.filter((b) => b.show).map(({ key, label }) => (
                    <SwatchPicker
                      key={key}
                      label={label}
                      bandKey={key}
                      selected={bandColors[key]}
                      onChange={handleColorChange}
                      allowedColors={getAllowedColors(key)}
                    />
                  ))}
                </div>

                <hr />

                {/* Result card */}
                {result && (
                  <div className="rc-result-card" >
                    <div style={{ fontSize: "12px", opacity: 0.7, marginBottom: "4px" }}>Nilai Resistansi</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                      <span className="rc-result-value">{result.formatted.value}</span>
                      <span className="rc-result-unit">{result.formatted.unit}</span>
                    </div>
                    <div className="rc-result-row">
                      {result.tolerance && (
                        <div className="rc-result-badge">
                          <b>Toleransi</b>
                          {result.tolerance}
                        </div>
                      )}
                      {result.minR && result.maxR && (
                        <div className="rc-result-badge">
                          <b>Rentang Nilai</b>
                          {result.minR.value} {result.minR.unit} — {result.maxR.value} {result.maxR.unit}
                        </div>
                      )}
                      {numBands === 6 && result.tempCoeff && (
                        <div className="rc-result-badge">
                          <b>Koefisien Suhu</b>
                          {result.tempCoeff}
                        </div>
                      )}
                    </div>
                    {/* Mini chip preview */}
                    <div style={{ display: "flex", gap: "6px", marginTop: "14px", alignItems: "center" }}>
                      {bands.filter((b) => b.show).map(({ key }) => (
                        <div
                          key={key}
                          className="rc-band-chip"
                          style={{ backgroundColor: colorCodes[bandColors[key]]?.hex || "#ccc" }}
                          title={colorCodes[bandColors[key]]?.colorAr}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ── IMAGE COLUMN — original images, unchanged ── */}
              <div className="col">
                {numBands === 3 && (
                  <img
                    loading="lazy"
                    src={band_3}
                    alt="3-Band Resistor"
                    style={{
                      width: "100%",
                      maxHeight: isMobile ? "400px" : "500px",
                      objectFit: "contain",
                    }}
                  />
                )}
                {numBands === 4 && (
                  <img
                    loading="lazy"
                    src={band_4}
                    alt="4-Band Resistor"
                    style={{
                      width: "100%",
                      maxHeight: isMobile ? "300px" : "500px",
                      objectFit: "contain",
                    }}
                  />
                )}
                {numBands === 5 && (
                  <img
                    loading="lazy"
                    src={band_5}
                    alt="5-Band Resistor"
                    style={{
                      width: "100%",
                      maxHeight: isMobile ? "300px" : "500px",
                      objectFit: "contain",
                    }}
                  />
                )}
                {numBands === 6 && (
                  <img
                    loading="lazy"
                    src={band_6}
                    alt="6-Band Resistor"
                    style={{
                      width: "100%",
                      maxHeight: isMobile ? "300px" : "500px",
                      objectFit: "contain",
                    }}
                  />
                )}
              </div>
            </div>
            {/* ===== END CALCULATOR ROW ===== */}
          </div>
        </div>

        <div className="col">
          <SecondAds />
          <MainCalculatorsList />
        </div>

        <div className="row"><div className="col"><ParallelResistorCalculator /></div></div>
        <div><br /></div>
        <div className="row"><div className="col"><SeriesResistorCalculator /></div></div>
        <div><br /></div>
        <div className="row"><div className="col"><ResistanceCalculator /></div></div>
        <div><br /></div>
        <div className="row">
          <div className="col-12 col-md-8"><RelatedLinks /></div>
        </div>
      </div>

      {/* ===== ORIGINAL INFO SECTIONS — unchanged ===== */}
      <div className="col-12 col-md-8">
        <div className="info-sections-container">
          <div className="info-section">
            <div className="info-section-header">
              <h3 style={{ fontSize: "16px" }}>Resistor Seri</h3>
            </div>
            <div className="info-section-body">
              <p>
                Ketika resistor dihubungkan secara seri, resistansi ekivalen adalah
                jumlah dari nilai resistansi individual. Dengan kata lain, Anda dapat menghitung resistansi
                total dengan menjumlahkan nilai resistansi. Misalnya, jika
                Anda memiliki dua resistor dengan nilai 10 ohm dan 15 ohm yang terhubung secara seri,
                resistansi ekivalennya adalah:
                <strong>10 ohm + 15 ohm = 25 ohm</strong>.
              </p>
              <p>
                Contoh lain: Jika Anda memiliki tiga resistor dengan nilai 5 ohm, 10 ohm, dan 20 ohm
                secara seri, resistansi ekivalennya adalah:
                <strong>5 ohm + 10 ohm + 20 ohm = 35 ohm</strong>.
              </p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header">
              <h3 style={{ fontSize: "16px" }}>Resistor Paralel</h3>
            </div>
            <div className="info-section-body">
              <p>
                Ketika resistor dihubungkan secara paralel, kebalikan dari resistansi
                ekivalen sama dengan jumlah kebalikan dari nilai resistansi individual. Anda dapat
                menghitung resistansi ekivalen menggunakan rumus:
                <strong>1/R_total = 1/R1 + 1/R2 + ... + 1/Rn</strong>, di mana{" "}
                <strong>R_total</strong> adalah resistansi ekivalen, dan{" "}
                <strong>R1, R2, ..., Rn</strong> adalah resistansi individual.
              </p>
              <p>
                Misalnya, jika Anda memiliki dua resistor dengan nilai 4 ohm dan 6 ohm
                yang terhubung secara paralel, resistansi ekivalen dapat dihitung sebagai berikut:
                <strong>1/R_total = 1/4 + 1/6</strong>, sehingga{" "}
                <strong>R_total = 2.4 ohm</strong>.
              </p>
              <p>
                Contoh lain: Jika Anda memiliki tiga resistor dengan nilai 2 ohm, 3 ohm, dan 6 ohm
                secara paralel, resistansi ekivalennya adalah:
                <strong>1/R_total = 1/2 + 1/3 + 1/6</strong>, sehingga{" "}
                <strong>R_total = 1 ohm</strong>.
              </p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header">
              <h3 style={{ fontSize: "16px" }}>Kalkulator Resistansi Konduktor</h3>
            </div>
            <div className="info-section-body">
              <p>
                Kalkulator resistansi konduktor membantu Anda menghitung resistansi konduktor berdasarkan
                ukuran dan konduktivitasnya. Resistansi konduktor dapat berbeda berdasarkan
                jenis bahan yang digunakan, panjang, dan diameter konduktor. Untuk menggunakan kalkulator ini, masukkan
                informasi berikut:
              </p>
              <ul>
                <li>Panjang konduktor</li>
                <li>Diameter konduktor</li>
                <li>Konduktivitas bahan</li>
              </ul>
              Setelah memasukkan nilai-nilai ini, tekan tombol "Hitung Resistansi" untuk mendapatkan nilai
              yang akurat dari resistansi konduktor.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResistorCalculator;